import { TranslateService } from '@ngx-translate/core';
import { Injectable, Injector } from '@angular/core';
import { map, mergeMap, switchMap, tap, BehaviorSubject, of, Observable } from 'rxjs';
import { AuthUser } from '../models/auth.user.model';
import { BaseApiService } from './baseapi.service';
import { Roles } from '../enums/role.enum';
import { ApplicationPermissions } from '../application-permission';
import { UnitedWay } from '../models/unitedway.model';
import { ChangePasswordRequest } from '../api/models';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApiService {

  private _authUser!: AuthUser | null;
  private _userState = new BehaviorSubject<AuthUser | null>(null); // use to dispatch event
  private _userPermissions = new BehaviorSubject<string[]>([]);
  private _canResetPassword!: boolean;

  constructor(
    protected injector: Injector,
    protected translateService: TranslateService
  ) {
    super(injector);
  }

  hasDonorRole() {
    return this._authUser && this._authUser.roles && this._authUser.roles.includes(Roles.Donor);
  }

  authenticateUser(loginRequest: { username: string; password: string }) {
    const url = `${this.baseApiUrl}/authenticate/admin/login`;
    const dataToSend = {
      username: loginRequest.username,
      password: loginRequest.password,
    };
    return this.httpPost(url, dataToSend).pipe(
      tap((user: any) => {
        if (!user) {
          return;
        }
        this._authUser = this.mapUserModel(user.data);
        localStorage.setItem('token', this._authUser.token);
        this._userState.next(this._authUser);
      }),
      mergeMap(() => this.loadProfileWithPermission())
    );
  }

  resetPassword(userId: number | string, password: string) {
    const url = `${this.baseApiUrl}/authenticate/user/${userId}/resetpassword`;
    const dataToSend = {
      password,
    };

    return this.httpPost(url, dataToSend);
  }

  setPassword(
    passwordReset: { password: string; confirmPassword: string },
    token: string,
    email: string
  ) {
    const url = `${this.baseApiUrl}/account/setpassword`;
    const dataToSend = {
      password: passwordReset.password,
      token,
      email,
    };
    return this.httpPost(url, dataToSend);
  }


  resetPasswordPasswordGuid(
    passwordReset: { password: string; confirmPassword: string },
    token: string,
    email: string
  ) {
    const url = `${this.baseApiUrl}/account/setpassword/v2`;
    const dataToSend = {
      password: passwordReset.password,
      token,
      email,
    };
    return this.httpPost(url, dataToSend);
  }

  verifyToken(
    token: string,
    email: string
  ) {
    const url = `${this.baseApiUrl}/account/verify-reset-token`;
    const dataToSend = {
      token,
      email,
    };
    return this.httpPost(url, dataToSend);
  }

  verifyNewToken(
    token: string,
    email: string
  ) {
    const url = `${this.baseApiUrl}/account/verify-reset-token/v2`;
    const dataToSend = {
      token,
      email,
    };
    return this.httpPost(url, dataToSend);
  }

  forgotPassword(email: string) {
    const url = `${this.baseApiUrl}/account/admin/forgotpassword/v2`;
    const dataToSend = { email };
    return this.httpPost(url, dataToSend);
  }

  changePassword(changeRequest: ChangePasswordRequest) {
    const url = `${this.baseApiUrl}/account/changepassword`;
    return this.httpPost(url, changeRequest);
  }

  fetchProfile(): Observable<AuthUser | null> {
    const url = `${this.baseApiUrl}/donor/profile`;
    return this.httpAuthGet(url).pipe(
      map(
        (res: any) => {
          let user: AuthUser = {
            token: localStorage.getItem('token') ?? '',
          };
          user.firstName = res.data.firstName;
          user.lastName = res.data.lastName;
          user.language = res.data.languagePref;
          user.userName = res.data.userName;
          user.roles = res.data.roles;
          user.isSSOUser = res.data.isSSOUser;
          this._authUser = { ...this._authUser, ...user };
          this._userState.next(this._authUser);
          localStorage.setItem('isSSOUser', res.data.isSSOUser);
          return this._authUser;
        }
      )
    );
  }

  loadProfileWithPermission(): Observable<AuthUser | null> {
    return this.loadUserPermissions().pipe(mergeMap(_ => this.loadUserInfo()), mergeMap(_ => this.fetchProfile()));
  }

  refreshToken(token?: string | null): Observable<string> {
    const url = `${this.baseApiUrl}/authenticate/refresh-token`;
    return this.httpAuthPost(url, {token: token}).pipe(
      map(resp => resp.data.token),
      tap((refreshedToken) => {
        localStorage.setItem('token', refreshedToken);
        if (this._authUser) {
          this._authUser = { ...this._authUser, token: refreshedToken };
          this._userState.next(this._authUser);
        }
      })
    );
  }

  getUnitedWayList(): Observable<UnitedWay[]> {
    const url = `${this.baseApiUrl}/administration/Admin/unitedway/list`;
    return this.httpAuthGet(url).pipe(map(res => res.data));
  }

  switchContext(unitedWayId: number) {
    const url = `${this.baseApiUrl}/authenticate/admin/swtichUWContext`;
    const dataToSend = {
      unitedWayId
    };
    return this.httpPost(url, dataToSend).pipe(
      tap((user: any) => {
        if (!user) {
          return;
        }
        //this._authUser = this.mapUserModel(user.data);
        if (this._authUser) {
          this._authUser.token = user.data.token;
          this._authUser.unitedWayId = user.data.unitedWayId;
          localStorage.setItem('token', this._authUser.token);
        }
        this._userState.next(this._authUser);
      })
    );
  }


  logOut() {
      const url = `${this.baseApiUrl}/authenticate/log-out`;
    return this.httpAuthPost(url, {token: localStorage.getItem('token')}).pipe(
      tap(_ => {
        this._userState.next(null);
        this._authUser = null;
        this._userPermissions.next([]);
        localStorage.clear();
      })
    );
  }

  sso_saml_logOut() {
    let token = localStorage.getItem('token');
    this._userState.next(null);
    this._authUser = null;
    this._userPermissions.next([]);
    localStorage.clear();
    const url = `${this.baseApiUrl}/saml/auth/logout?token=${token}`;
    window.location.href = url; //redirect the user
  }


  togglerLangugage(lang: string): Observable<any> {
    const url = `${this.baseApiUrl}/authentication/user/language/${lang}`;
    return this.httpAuthPost(url, null);
  }

  getUser = () => this._authUser;

  canUserResetPassword = () => this._canResetPassword;

  isUserLoggedId = () => !!this._authUser;

  getUserState = () => this._userState.asObservable();

  getUserPermissionState = () => this._userPermissions.asObservable();

  getUserPermissions = () => this._userPermissions.value;

  private mapUserModel(res: any): AuthUser {
    const output: AuthUser = { ...res };
    return output;
  }

  updateProfile(profile: { firstName: string; lastName: string }) {
    if (this._authUser) {
      this._authUser.firstName = profile.firstName;
      this._authUser.lastName = profile.lastName;
      this._userState.next(this._authUser);
    }
  }

  loadUserPermissions(): Observable<string[]> {
    const url = `${this.baseApiUrl}/admin/permissions`;
    return this.httpAuthGet(url).pipe(map(res => res.data), tap(permissions => {
      this._userPermissions.next(permissions);
      this._canResetPassword = permissions.includes(ApplicationPermissions.setTempPassword);
    }));
  }

  loadUserInfo(): Observable<string[]> {
    const url = `${this.baseApiUrl}/Administration/admin/user/info`;
    return this.httpAuthGet(url)
      //return this.httpAuthGet("assets/mock/user-info.json")
      .pipe(map(res => res.data), tap((userinfo: any) => {
        this._authUser = this._authUser ? { ...this._authUser, ...this.updateCampaignList(userinfo) } as AuthUser : { ...this.updateCampaignList(userinfo) } as AuthUser;
        this._userState.next(this._authUser);
        let permission = this._userPermissions.getValue();
        if (userinfo.roleCode === Roles.UnitedWayAdministrator || userinfo.roleCode === Roles.SystemAdministrator) {
          permission = [...permission, ApplicationPermissions.unitedWayAdmin];
          this._userPermissions.next(permission);
        }
        if (userinfo.roleCode === Roles.ReportAdministrator) {
          permission = [...permission, ApplicationPermissions.reportAdmin];
          this._userPermissions.next(permission);
        }
        if (userinfo.roleCode === Roles.Canvasser) {
          permission = [...permission, ApplicationPermissions.canvasserAdmin];
          this._userPermissions.next(permission);
        }
      }))
  }

  updateCampaignList(userInfo: any) {
    let output = userInfo;
    if (userInfo.campaigns) {
      output.campaigns = userInfo.campaigns.map((c: any) => c.campaignId);
    }
    return output;
  }

}
